import {
  WORKSPACE_ZOOM_MAX,
  WORKSPACE_ZOOM_MIN,
  ELEMENT_PANEL_TOOLBAR_HEIGHT,
  PANEL_MARGIN,
  TOOLBAR_HEIGHT,
} from '@/components/canvas/shared/constants/legacyConfig';
import type { WorldBounds } from '@/components/canvas/page/pageBounds';

/** 适应计算时预留的 UI 边距（屏幕像素） */
export interface WorkspaceFitInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export type WorkspaceChromeInsetsMode = 'edit' | 'fullscreen' | 'none';

const ZERO_INSETS: WorkspaceFitInsets = { top: 0, right: 0, bottom: 0, left: 0 };

/** 编辑态：顶部元素条、底部页导航条、右侧模式切换条 */
export const WORKSPACE_EDIT_FIT_INSETS: WorkspaceFitInsets = {
  top: PANEL_MARGIN + ELEMENT_PANEL_TOOLBAR_HEIGHT + PANEL_MARGIN,
  bottom: PANEL_MARGIN + TOOLBAR_HEIGHT + PANEL_MARGIN,
  left: PANEL_MARGIN * 2,
  right: PANEL_MARGIN + 44 + PANEL_MARGIN,
};

/** 全屏播放：顶部元素条仍可见；底部为播放控制条占位 */
export const WORKSPACE_FULLSCREEN_FIT_INSETS: WorkspaceFitInsets = {
  top: PANEL_MARGIN + ELEMENT_PANEL_TOOLBAR_HEIGHT + PANEL_MARGIN,
  bottom: PANEL_MARGIN + TOOLBAR_HEIGHT,
  left: PANEL_MARGIN * 2,
  right: PANEL_MARGIN + 44 + PANEL_MARGIN,
};

export function resolveWorkspaceFitInsets(mode: WorkspaceChromeInsetsMode): WorkspaceFitInsets {
  switch (mode) {
    case 'edit':
      return WORKSPACE_EDIT_FIT_INSETS;
    case 'fullscreen':
      return WORKSPACE_FULLSCREEN_FIT_INSETS;
    case 'none':
      return ZERO_INSETS;
  }
}

export function shrinkContainerForFitInsets(
  width: number,
  height: number,
  insets: WorkspaceFitInsets,
): { width: number; height: number } {
  return {
    width: Math.max(1, width - insets.left - insets.right),
    height: Math.max(1, height - insets.top - insets.bottom),
  };
}

export interface ComputeFitViewportParams {
  containerWidth: number;
  containerHeight: number;
  bounds: WorldBounds;
  /** 在已扣除工具条后的安全区内再留少量边距；全屏贴边时为 1 */
  marginFactor?: number;
  insets?: WorkspaceFitInsets;
  maxZoom?: number;
}

/** 将世界坐标包围盒缩放并平移，使其落在视口安全区内居中 */
export function computeFitViewport({
  containerWidth,
  containerHeight,
  bounds,
  marginFactor = 1,
  insets = ZERO_INSETS,
  maxZoom = WORKSPACE_ZOOM_MAX,
}: ComputeFitViewportParams): { zoom: number; panX: number; panY: number } {
  const inner = shrinkContainerForFitInsets(containerWidth, containerHeight, insets);
  const ratio = Math.min(inner.width / bounds.width, inner.height / bounds.height);
  const zoom = Math.max(WORKSPACE_ZOOM_MIN, Math.min(maxZoom, ratio * marginFactor));
  const scaledW = bounds.width * zoom;
  const scaledH = bounds.height * zoom;
  return {
    zoom,
    panX: insets.left + (inner.width - scaledW) / 2 - bounds.x * zoom,
    panY: insets.top + (inner.height - scaledH) / 2 - bounds.y * zoom,
  };
}
